import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_WARNING,
} from 'ui-components/services/notification-service';

import { messages } from './messages';
import { useScanSound } from '../../../../../../components/scan-to-receive/scan-product/use-scan-sound';
import { useModal } from '../../../../../../hooks/use-modal';
import { useRequiredManualConfirmationSetting, useVerifyItemsBySetting } from '../../../../../../hooks/use-user-setting';
import { availableModes, warningTypes } from '../../../../../../scan/constants';
import { ItemSearchService } from '../../../../../../scan/services/item-search.service';
import { MathService } from '../../../../../../services/math-service';
import * as EntitiesActions from '../../../../../../store/entities/entities.actions';
import Global from '../../../../../../store/global-selectors';
import { Workflows } from '../../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../../tracking/use-workflow-tracking';

import type { ReactNode } from 'react';

export type FulfillmentPlanItem = {
  fulfillmentPlanItemId: string;
  productId?: number;
  name: string;
  sku: string;
  upc?: string;
  quantity: number;
  productThumbnailUrl?: string;
};

type ScanVerificationContextValue = {
  items: FulfillmentPlanItem[];
  isInVerificationMode: boolean;
  openVerificationMode: () => void;
  closeVerificationMode: () => void;
  isVerified: boolean;
  currentlyVerifyingItemId: string | null;
  verifiedItemCounts: Record<string, number>;
  verifyItem: (fulfillmentPlanItemId: string, count: number) => void;
  verifyAllItems: () => void;
  resetItemCount: (fulfillmentPlanItemId: string) => void;
  clearAllItems: () => void;
  requireManualMarkingAsVerified: boolean;
  warningType: string | null;
  showInvalidScanModal: boolean;
  closeInvalidScanModal: () => void;
  isSubmitting: boolean;
  submitVerification: () => void;
  isItemVerified: (fulfillmentPlanItemId: string) => boolean;
  getVerifiedCount: (fulfillmentPlanItemId: string) => number;
  onScan: (productCode: string) => void;
};

const ScanVerificationContext = createContext<ScanVerificationContextValue | undefined>(undefined);

type ScanVerificationContextProviderProps = {
  children: ReactNode;
  fulfillmentPlanId: string;
  items: FulfillmentPlanItem[];
};

export const ScanVerificationContextProvider = ({ children, fulfillmentPlanId, items }: ScanVerificationContextProviderProps) => {
  const dispatch = useDispatch();

  const [verifiedItemCounts, setVerifiedItemCounts] = useState<Record<string, number>>({});
  const [currentlyVerifyingItemId, setCurrentlyVerifyingItemId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scanByFields = useVerifyItemsBySetting();
  const { isOpen: isInVerificationMode, open: openVerificationMode, close: closeVerificationMode } = useModal();

  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const sellerId = useSelector(Global.selectSellerSellerId);
  const workflowTracker = useTrackWorkflow(Workflows.SCAN_TO);

  const [warningType, setWarningType] = useState<string | null>(null);
  const { open: openInvalidScanModal, isOpen: showInvalidScanModal, close } = useModal();

  const playSound = useScanSound();

  const requireManualMarkingAsVerified = useRequiredManualConfirmationSetting();

  const rowTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRowTimeout = useCallback(() => {
    if (rowTimeoutRef.current) clearTimeout(rowTimeoutRef.current);
  }, []);

  const resetScanData = useCallback(() => {
    setWarningType(null);
    setVerifiedItemCounts({});
    setCurrentlyVerifyingItemId(null);
  }, []);

  const clearAllItems = useCallback(() => {
    workflowTracker(WorkflowSteps.CLEAR_ALL, {
      itemCount: items.length,
    });
    resetScanData();
  }, [workflowTracker, items.length, resetScanData]);

  const onCloseVerificationMode = useCallback(() => {
    clearRowTimeout();
    resetScanData();
    closeVerificationMode();
  }, [clearRowTimeout, resetScanData, closeVerificationMode]);

  const handleCloseVerificationMode = useCallback(() => {
    workflowTracker(WorkflowSteps.CANCEL, {
      reason: 'Verification closed',
    });
    onCloseVerificationMode();
  }, [workflowTracker, onCloseVerificationMode]);

  const handleOpenVerificationMode = useCallback(() => {
    workflowTracker(WorkflowSteps.START, {
      sellerId,
      scanMode: availableModes.verify,
      isFromOrder: true,
      inVerifyOnlyMode: true,
    });
    openVerificationMode();
  }, [workflowTracker, sellerId, openVerificationMode]);

  const submitVerification = useCallback(async () => {
    try {
      setIsSubmitting(true);

      await axios.post('/verify/fulfillmentPlan', {
        isVerified: true,
        fulfillmentPlanId,
      });

      const totalItemCount = items.reduce((acc, item) => acc + item.quantity, 0);
      const verifiedCount = Object.values(verifiedItemCounts).reduce((acc, count) => acc + count, 0);

      workflowTracker(WorkflowSteps.COMPLETE, {
        totalItemCount,
        verifiedCount,
      });

      const salesOrderIds = fulfillmentPlan?.salesOrdersFulfilledIds ?? [];
      if (salesOrderIds.length > 0) {
        dispatch(EntitiesActions.bulkRequestEntities({ salesOrderIds }));
      }

      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        content: <FormattedMessage {...messages.shipmentItemsVerified} />,
        duration: NOTIFICATION_DURATIONS.SHORT,
      });

      onCloseVerificationMode();
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_WARNING,
        content: <FormattedMessage {...messages.shipmentWarningToast} />,
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    fulfillmentPlanId,
    fulfillmentPlan?.salesOrdersFulfilledIds,
    onCloseVerificationMode,
    dispatch,
    workflowTracker,
    items,
    verifiedItemCounts,
  ]);

  const isVerified = useMemo(() => {
    return items.every((item) => {
      const verifiedCount = verifiedItemCounts[item.fulfillmentPlanItemId] ?? 0;
      return verifiedCount >= item.quantity;
    });
  }, [items, verifiedItemCounts]);

  const verifyItem = useCallback(
    (fulfillmentPlanItemId: string, count: number) => {
      setCurrentlyVerifyingItemId(fulfillmentPlanItemId);
      clearRowTimeout();

      const VERIFICATION_TIMEOUT = 1000; // 1 second
      rowTimeoutRef.current = setTimeout(() => {
        setCurrentlyVerifyingItemId(null);
      }, VERIFICATION_TIMEOUT);

      const newCounts = {
        ...verifiedItemCounts,
        [fulfillmentPlanItemId]: count,
      };

      const isEverythingVerified = items.every((item) => {
        const itemCount = newCounts[item.fulfillmentPlanItemId] ?? 0;
        return itemCount >= item.quantity;
      });

      setVerifiedItemCounts(newCounts);

      if (isEverythingVerified && !requireManualMarkingAsVerified) {
        clearRowTimeout();
        playSound('success');
        submitVerification();
      } else {
        playSound('verified');
      }
    },
    [clearRowTimeout, verifiedItemCounts, items, requireManualMarkingAsVerified, playSound, submitVerification],
  );

  const resetItemCount = useCallback((fulfillmentPlanItemId: string) => {
    setVerifiedItemCounts((prev) => ({
      ...prev,
      [fulfillmentPlanItemId]: 0,
    }));
    setCurrentlyVerifyingItemId(null);
  }, []);

  const verifyAllItems = useCallback(() => {
    const newCounts = items.reduce(
      (acc, item) => ({
        ...acc,
        [item.fulfillmentPlanItemId]: item.quantity,
      }),
      {},
    );

    const totalQuantity = items.reduce((acc, item) => acc + item.quantity, 0);

    workflowTracker(WorkflowSteps.VERIFY_ALL, {
      itemCount: items.length,
      totalQuantity,
    });

    setVerifiedItemCounts(newCounts);
    if (!requireManualMarkingAsVerified) {
      playSound('success');
      submitVerification();
    } else {
      playSound('verified');
    }
  }, [items, playSound, requireManualMarkingAsVerified, submitVerification, workflowTracker]);

  const closeInvalidScanModal = useCallback(() => {
    close();
    setWarningType(null);
  }, [close]);

  const getVerifiedCount = useCallback(
    (fulfillmentPlanItemId: string) => {
      return verifiedItemCounts[fulfillmentPlanItemId] ?? 0;
    },
    [verifiedItemCounts],
  );

  const isItemVerified = useCallback(
    (fulfillmentPlanItemId: string) => {
      const item = items.find((i) => i.fulfillmentPlanItemId === fulfillmentPlanItemId);
      if (!item) return false;

      const verifiedCount = getVerifiedCount(fulfillmentPlanItemId);
      return verifiedCount >= item.quantity;
    },
    [items, getVerifiedCount],
  );

  const onScan = useCallback(
    (productCode: string) => {
      if (!productCode?.length) {
        return;
      }

      const itemExistsInFP = ItemSearchService.findItemInFP(productCode, items, scanByFields) as FulfillmentPlanItem | undefined;
      if (!itemExistsInFP) {
        setWarningType(warningTypes.incorrectProduct);
        playSound('error');
        openInvalidScanModal();
        return;
      }

      const isItemAlreadyVerified = isItemVerified(itemExistsInFP.fulfillmentPlanItemId);
      if (isItemAlreadyVerified) {
        setWarningType(warningTypes.extraProduct);
        playSound('error');
        openInvalidScanModal();
        return;
      }

      const newVerifiedCount = MathService.clamp(getVerifiedCount(itemExistsInFP.fulfillmentPlanItemId) + 1, 0, itemExistsInFP.quantity);

      workflowTracker(WorkflowSteps.ITEM_VERIFIED_SCANNED, {
        itemId: itemExistsInFP.fulfillmentPlanItemId,
        verifiedCount: newVerifiedCount,
      });

      verifyItem(itemExistsInFP.fulfillmentPlanItemId, newVerifiedCount);
    },
    [items, scanByFields, isItemVerified, verifyItem, getVerifiedCount, playSound, openInvalidScanModal, workflowTracker],
  );

  useEffect(() => {
    return () => {
      onCloseVerificationMode();
    };
  }, [onCloseVerificationMode]);

  const value = useMemo(
    () => ({
      items,
      isInVerificationMode,
      requireManualMarkingAsVerified,
      openVerificationMode: handleOpenVerificationMode,
      closeVerificationMode: handleCloseVerificationMode,
      currentlyVerifyingItemId,
      isVerified,
      warningType,
      showInvalidScanModal,
      verifyItem,
      resetItemCount,
      verifyAllItems,
      clearAllItems,
      onScan,
      openInvalidScanModal,
      closeInvalidScanModal,
      submitVerification,
      isItemVerified,
      getVerifiedCount,
      isSubmitting,
      verifiedItemCounts,
    }),
    [
      items,
      isInVerificationMode,
      requireManualMarkingAsVerified,
      handleOpenVerificationMode,
      handleCloseVerificationMode,
      currentlyVerifyingItemId,
      isVerified,
      warningType,
      showInvalidScanModal,
      verifyItem,
      resetItemCount,
      verifyAllItems,
      clearAllItems,
      onScan,
      openInvalidScanModal,
      closeInvalidScanModal,
      submitVerification,
      isItemVerified,
      getVerifiedCount,
      isSubmitting,
      verifiedItemCounts,
    ],
  );

  return <ScanVerificationContext.Provider value={value}>{children}</ScanVerificationContext.Provider>;
};

export const useScanVerificationContext = () => {
  const context = useContext(ScanVerificationContext);
  if (!context) {
    throw new Error('useScanVerificationContext must be used within ScanVerificationContextProvider');
  }
  return context;
};
